import React from 'react'
import { Link } from 'react-router-dom'

// Renders the list of CatalogEntrySource rows attached to a catalog entry.
// Shape comes directly from CatalogEntriesController#serialize_source.
//
// - Response (interview) sources attribute to the person + initiative and
//   link to the initiative detail page when an initiative_id is present.
//   The per-response detail page from the old design no longer exists;
//   the initiative is the closest navigational target for now.
// - Artifact sources link to the signed file URL and attribute to the
//   filename.
function InterviewIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h6M21 12a8 8 0 01-12.163 6.82L3 20l1.18-5.837A8 8 0 1121 12z" />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h4m5-14H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8l-6-6z" />
    </svg>
  )
}

function SourceCard({ source }) {
  // Phase 1 collapsed InterviewResponse into Response, so the polymorphic
  // type string the backend now sends is 'Response'.
  const isInterview = source.source_type === 'Response'
  const isArtifact  = source.source_type === 'Artifact'

  const Icon = isInterview ? InterviewIcon : DocumentIcon

  const attribution =
    isInterview
      ? [ source.person_name, source.initiative_name ? `Interview · ${source.initiative_name}` : 'Interview' ]
          .filter(Boolean)
          .join(' — ')
      : [ source.artifact_name || 'Document', source.person_name ].filter(Boolean).join(' — ')

  const linkProps = (() => {
    if (isInterview && source.initiative_slug) {
      return { as: Link, to: `/initiatives/${source.initiative_slug}` }
    }
    if (isArtifact && source.file_url) {
      return { as: 'a', href: source.file_url, target: '_blank', rel: 'noopener noreferrer' }
    }
    return null
  })()

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
        <span className={isInterview ? 'text-accent-500' : 'text-emerald-600'}>
          <Icon />
        </span>
        <span>{isInterview ? 'Interview source' : 'Document source'}</span>
      </div>

      {source.excerpt && (
        <blockquote className="border-l-2 border-slate-200 pl-4 text-sm italic text-slate-700 leading-relaxed whitespace-pre-wrap">
          {source.excerpt}
        </blockquote>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span className="truncate">{attribution || '—'}</span>
        {linkProps && (
          linkProps.as === Link ? (
            <Link
              to={linkProps.to}
              className="shrink-0 ml-3 text-accent-600 hover:text-accent-500 font-medium"
            >
              View →
            </Link>
          ) : (
            <a
              href={linkProps.href}
              target={linkProps.target}
              rel={linkProps.rel}
              className="shrink-0 ml-3 text-accent-600 hover:text-accent-500 font-medium"
            >
              Open →
            </a>
          )
        )}
      </div>
    </div>
  )
}

export default function SourceProvenance({ sources }) {
  if (!sources || sources.length === 0) {
    return <p className="text-sm text-slate-500">No source passages attached yet.</p>
  }

  return (
    <div className="space-y-3">
      {sources.map((source) => (
        <SourceCard key={source.id} source={source} />
      ))}
    </div>
  )
}
